import axios from 'axios';
import { getConfig } from '../config';
import { AdminApiDomain, getAdminApiUrl } from '../config/ApiRouting';
import type { GpKeywordCpSearch } from '../models/Coupang';

export interface ApiResponse {
  products: GpKeywordCpSearch[];
}

export type DirectCoupangSearchStatus =
  | 'RESERVED'
  | 'SUBMITTED'
  | 'WRITING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'SUBMISSION_UNKNOWN';

export interface DirectCoupangSearchSource {
  name: string;
  brand: string;
  subcategory?: string;
  brandAliases?: string[];
}

export interface StartDirectCoupangSearchRequest {
  requestId: string;
  gpProductId: string;
  refresh: boolean;
  source: DirectCoupangSearchSource;
}

export interface DirectCoupangSearchExecution {
  executionId: string;
  requestId: string;
  gpProductId: string;
  status: DirectCoupangSearchStatus;
  resultCount?: number;
  errorCategory?: string;
}

const DIRECT_SEARCH_STATUSES: ReadonlySet<string> = new Set([
  'RESERVED',
  'SUBMITTED',
  'WRITING',
  'SUCCEEDED',
  'FAILED',
  'SUBMISSION_UNKNOWN',
]);

export class CpSearchClient {
  private readonly baseUrl: string;
  private readonly apiKey: string;

  constructor(baseUrl = getAdminApiUrl(AdminApiDomain.SOURCING), apiKey = getConfig().apiKey) {
    this.baseUrl = baseUrl;
    this.apiKey = apiKey;
  }

  // Get GP Keyword CP Search results by Glowpick Product ID
  async getGpKeywordCpSearch(gpProductId: string): Promise<ApiResponse> {
    const url = `${this.baseUrl}/sourcing/gpKeywordCpSearch?gpProductId=${gpProductId}`;
    console.info('🌐 API Request:', url);
    const response = await axios.get(url, {
      headers: { 'X-API-Key': this.apiKey },
    });

    // The server returns { products: GpKeywordCpSearch[] }
    console.info(`🌐 API response with ${response.data.products?.length || 0} products received`);
    return response.data;
  }

  // Delete GP Keyword CP Search results
  async deleteGpKeywordCpSearch(gpProductId: string): Promise<{ success: boolean; message?: string }> {
    const url = `${this.baseUrl}/sourcing/gpKeywordCpSearch?gpProductId=${gpProductId}`;
    console.info('🗑️ API Request (DELETE):', url);

    try {
      await axios.delete(url, {
        headers: { 'X-API-Key': this.apiKey },
      });
      console.info('✅ GP Keyword CP Search results deleted successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Failed to delete GP Keyword CP Search results:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async startDirectCoupangSearch(request: StartDirectCoupangSearchRequest): Promise<DirectCoupangSearchExecution> {
    try {
      const response = await axios.post<DirectCoupangSearchExecution>(
        `${this.baseUrl}/sourcing/gpKeywordCpSearch/searches`,
        request,
        {
          headers: { 'Content-Type': 'application/json', 'X-API-Key': this.apiKey },
        },
      );
      return this.requireDirectSearchExecution(response.data, request.requestId, request.gpProductId);
    } catch {
      throw new Error('Coupang search could not be started');
    }
  }

  async refreshDirectCoupangSearch(
    executionId: string,
    requestId: string,
    gpProductId: string,
  ): Promise<DirectCoupangSearchExecution> {
    try {
      const response = await axios.post<DirectCoupangSearchExecution>(
        `${this.baseUrl}/sourcing/gpKeywordCpSearch/searches/${encodeURIComponent(executionId)}/refresh`,
        { requestId },
        { headers: { 'Content-Type': 'application/json', 'X-API-Key': this.apiKey } },
      );
      return this.requireDirectSearchExecution(response.data, requestId, gpProductId);
    } catch {
      throw new Error('Coupang search status could not be refreshed');
    }
  }

  private requireDirectSearchExecution(
    value: unknown,
    requestId: string,
    gpProductId?: string,
  ): DirectCoupangSearchExecution {
    if (
      !value ||
      typeof value !== 'object' ||
      typeof (value as DirectCoupangSearchExecution).executionId !== 'string' ||
      typeof (value as DirectCoupangSearchExecution).requestId !== 'string' ||
      typeof (value as DirectCoupangSearchExecution).gpProductId !== 'string' ||
      !DIRECT_SEARCH_STATUSES.has((value as DirectCoupangSearchExecution).status) ||
      (value as DirectCoupangSearchExecution).requestId !== requestId ||
      (gpProductId !== undefined && (value as DirectCoupangSearchExecution).gpProductId !== gpProductId)
    ) {
      throw new Error('Coupang search returned an invalid status');
    }
    return value as DirectCoupangSearchExecution;
  }
}

export const cpSearchClient = new CpSearchClient();
